import React, { useState } from 'react';

import { VHE_DEFAULTS, VHE_FALLBACK } from '../Viewer/virtualHe';
import { CardTitle, Slider } from './ChannelCard';
import { WindowRow } from './BloodChannelCard';

const num = (v, digits = 2) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Canal « HE virtuel » (src/components/Viewer/virtualHe.js), présenté comme une modalité : interrupteur (exclusif),
// canaux (noyaux : absorbance à 265 nm ; référence de diffusion ; éosine : fluorescence à 280 nm ; collagène : SHG,
// facultatif), k, échelle des noyaux, intensités de l'hématoxyline, de l'éosine et du safran. nucleiOnly : canal
// « Noyaux » (hématoxyline seule : 265 nm et référence).
export default function VirtualHeCard({
  channels, n, r, e, c, settings, active, error, onChange, onWindow, onToggle, onEstimate, collapsed, onCollapse, label,
  onRename, nucleiOnly = false,
}) {
  const [estimating, setEstimating] = useState(false);
  const estimate = async () => {
    setEstimating(true);
    try { await onEstimate(); } finally { setEstimating(false); }
  };
  const select = (key, value, label) => (
    <select value={value?.id || ''} aria-label={label} onChange={ev => onChange({ [key]: ev.target.value })}>
      {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
    </select>
  );
  // Copie 8 bits sans fenêtre d'origine connue : transmission absolue inconnue (voir BloodChannelCard).
  const editableWindow = ch => ch && ch.loader[0].dtype === 'Uint8' && (!ch.toOriginal || !!ch.sourceWindow);
  const unknown = [n, r].filter(ch => ch && !ch.scaleKnown);
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className={`swatch ${nucleiOnly ? 'nuclei' : 'vhe'}`}
          title={`Computed channel: ${nucleiOnly ? 'nuclei (virtual hematoxylin)' : 'virtual HE'}`} />
        <CardTitle name={label || (nucleiOnly ? 'Nuclei (hematoxylin)' : 'Virtual HE')} collapsed={collapsed}
          onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title="Show (hides the other modalities)">
          <input type="checkbox" checked={active} onChange={ev => onToggle(ev.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <p className="viewer-note" style={{ margin: 0 }}>
            {nucleiOnly
              ? 'Hematoxylin only = OD(265) − k × OD(reference) (nucleic acids), virtual HE colors.'
              : `Hematoxylin = OD(265) − k × OD(reference) (nucleic acids); eosin = fluorescence at 280 nm
            (proteins); saffron = SHG (collagen); colors by Beer-Lambert.`}
          </p>
          <div className="compare-row">
            <span className="compare-label">Nuclei</span>{select('n', n, 'Nuclei channel (265 nm absorbance)')}
          </div>
          <div className="compare-row">
            <span className="compare-label">Reference</span>{select('r', r, 'Reference channel (scattering)')}
          </div>
          {!nucleiOnly && (
            <>
              <div className="compare-row">
                <span className="compare-label">Eosin</span>{select('e', e, 'Eosin channel (280 nm fluorescence)')}
              </div>
              <div className="compare-row">
                <span className="compare-label">Collagen</span>
                <select value={c?.id || 'none'} aria-label="Collagen channel (SHG, saffron)"
                  onChange={ev => onChange({ c: ev.target.value })}>
                  <option value="none">None (no saffron)</option>
                  {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
                </select>
              </div>
            </>
          )}
          {error && <p className="viewer-note error">{error}</p>}
          {[n, r].filter(editableWindow).map(ch => <WindowRow key={ch.id} channel={ch} onWindow={onWindow} />)}
          {unknown.length > 0 && (
            <p className="viewer-note error" style={{ margin: 0 }}>
              {unknown.map(ch => ch.name).join(', ')}: 8-bit copy without its original window. Enter it (low – high from
              the copy's .window.json file), otherwise the absorbance is wrong.
            </p>
          )}
          <Slider label="k (reference scattering scaled to 265 nm)" value={settings.k ?? VHE_FALLBACK.k} min={0.3}
            max={3} step={0.01} reset={VHE_FALLBACK.k} format={v => `k = ${num(v)}`} onChange={v => onChange({ k: v })} />
          <Slider label="Nuclei scale (OD for full hematoxylin)" value={settings.hScale ?? VHE_FALLBACK.hScale}
            min={0.02} max={2} step={0.005} reset={VHE_FALLBACK.hScale} format={v => `${num(v, 3)} OD`}
            onChange={v => onChange({ hScale: v })} />
          <div className="channel-options">
            <button type="button" className="chip" onClick={estimate} disabled={estimating || !!error}
              title={`k = median OD ratio on the tissue; scale = 99.5th percentile of the nuclei signal${nucleiOnly ? '' : '; eosin and collagen windows: from the background (Otsu) to the 99.5th percentile of the signal'}`}>
              {estimating ? 'Computing…' : 'Estimate automatically'}
            </button>
          </div>
          <Slider label="Hematoxylin intensity" value={settings.hGain} max={6} step={0.05} reset={VHE_DEFAULTS.hGain}
            format={v => `× ${num(v)}`} onChange={v => onChange({ hGain: v })} />
          {!nucleiOnly && (
            <Slider label="Eosin intensity" value={settings.eGain} max={6} step={0.05}
              reset={VHE_DEFAULTS.eGain} format={v => `× ${num(v)}`} onChange={v => onChange({ eGain: v })} />
          )}
          {c && !nucleiOnly && (
            <Slider label="Saffron intensity (collagen)" value={settings.sGain} max={6} step={0.05}
              reset={VHE_DEFAULTS.sGain} format={v => `× ${num(v)}`} onChange={v => onChange({ sGain: v })} />
          )}
        </div>
      )}
    </div>
  );
}
